import { useIonRouter } from '@ionic/react';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RefresherEventDetail } from '@ionic/core';
import type { Swiper as SwiperType } from 'swiper';
import { FreeMode } from 'swiper';

import PetForm from '../../components/Pet/PetForm';
import SyncPets from '../../components/Pet/SyncPets';
import FormHeader from '../../components/UI/FormHeader';
import UserForm from '../../components/User/UserForm';
import { usePets } from '../../context/PetsContext';
import { useTutorial } from '../../context/TutorialContext';
import { useUser } from '../../context/UserContext';
import { usePetActions } from '../../hooks/Pet/usePetUtils';
import { useAlert } from '../../hooks/UI/useAlert';
import { useModal } from '../../hooks/UI/useModal';
import { Pet as PetType } from '../../types/Pet/types';
import {
  COVERAGE_EXPIRED_ALERT,
  hasExpiredCoverage,
  isDawerPet,
} from '../../utils/coverage';

export const useHomePage = () => {
  const { push } = useIonRouter();
  const { user } = useUser();
  const { pets, currentPet, setCurrentPet, mutate, isValidating } = usePets();
  const { tutorialDone } = useTutorial();
  const { isOpen, content, openModal, closeModal, modalProps } = useModal();
  const { alertState, showAlert, hideAlert } = useAlert();
  const isCoverageExpired = hasExpiredCoverage(currentPet);
  const { openNuevoSiniestro, openHistorial, openTelemedicina, openPlanPayment } =
    usePetActions({
      pet: currentPet,
      openModal,
      closeModal,
      showAlert,
    });

  const showCoverageBlockedAlert = useCallback(() => {
    showAlert({
      ...COVERAGE_EXPIRED_ALERT,
      buttons: [{ text: 'Entendido' }],
    });
  }, [showAlert]);

  const guardCoverageAction = useCallback(
    (action: () => void) => () => {
      if (isCoverageExpired) {
        showCoverageBlockedAlert();
        return;
      }

      action();
    },
    [isCoverageExpired, showCoverageBlockedAlert],
  );

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Referencia al Swiper
  const swiperRef = useRef<SwiperType | null>(null);

  // Configuración del Swiper
  const swiperConfig = useMemo(
    () => ({
      modules: [FreeMode],
      slidesPerView: 3.5,
      spaceBetween: 12,
      freeMode: {
        enabled: true,
        momentum: true,
        momentumVelocityRatio: 0.5,
      },
      grabCursor: true,
      onSwiper: (swiper: SwiperType) => {
        swiperRef.current = swiper;
      },
    }),
    [],
  );

  // Callback para seleccionar mascota o navegar
  const handlePetSelect = useCallback(
    (pet: PetType) => {
      if (currentPet && currentPet.id === pet.id) {
        push('/tabs/pet');
      } else {
        setCurrentPet(pet);
      }
    },
    [currentPet, setCurrentPet, push],
  );

  // Mascotas ordenadas (actual primero)
  const sortedPets = useMemo(() => {
    if (!pets) return [];
    if (!currentPet) return pets;

    return [currentPet, ...pets.filter((p: PetType) => p.id !== currentPet.id)];
  }, [pets, currentPet]);
  const isCurrentPetDawer = isDawerPet(currentPet);

  // Alert para completar datos personales
  const showPersonalDataAlert = useCallback(() => {
    const completeProfileButton = {
      text: 'Completar mis datos',
      handler: () =>
        openModal(<UserForm closeModal={closeModal} />, { navigation: false }),
    };

    showAlert({
      header: 'Mensaje importante',
      message: isCurrentPetDawer
        ? 'Complete sus datos personales para asegurar una mascota.'
        : 'Complete sus datos personales para asegurar una mascota. Si ya tiene mascotas aseguradas, sincronice las mismas para visualizarlas.',
      buttons: isCurrentPetDawer
        ? [completeProfileButton]
        : [
            {
              text: 'Sincronizar mascotas',
              role: 'cancel',
              handler: () =>
                openModal(
                  <SyncPets
                    header={
                      <FormHeader
                        icon={<ArrowsClockwiseIcon weight="fill" />}
                        title="Sincronizar"
                        subtitle="Traé tus mascotas a la app"
                      />
                    }
                  />,
                ),
            },
            completeProfileButton,
          ],
    });
  }, [isCurrentPetDawer, showAlert, openModal, closeModal]);

  // Handler único para abrir el modal del formulario
  const handleAddPet = useCallback(() => {
    if (isCoverageExpired) {
      showCoverageBlockedAlert();
      return;
    }

    showAlert({
      header: 'Nueva mascota',
      message: isCurrentPetDawer
        ? 'Por este medio podés contratar una nueva póliza para tu mascota. Si estás de acuerdo, podés continuar.'
        : 'Por este medio podés contratar una nueva póliza para tu mascota. ' +
          'Si ya tenés una mascota asegurada, este no es el camino para sincronizarla con la app. ' +
          'Si estás de acuerdo, podés continuar.',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
        },
        {
          text: 'Entendido',
          handler: () =>
            openModal(<PetForm closeModal={closeModal} />, {
              navigation: false,
            }),
        },
      ],
    });
  }, [
    isCurrentPetDawer,
    isCoverageExpired,
    showCoverageBlockedAlert,
    showAlert,
    openModal,
    closeModal,
  ]);

  // Refresher para pull-to-refresh
  const handleRefresh = useCallback(
    async (event: CustomEvent<RefresherEventDetail>) => {
      setIsRefreshing(true); // marcamos refresco manual

      await mutate(); // recarga datos

      setIsRefreshing(false); //  termina el refresher
      event.detail.complete();
    },
    [mutate],
  );

  // Al cambiar la mascota, el swiper debe volver al slide 0.
  // Existe un bug: si la primera mascota está fuera del viewport,
  // el swiper vuelve solo al elemento seleccionado y genera un “salto”.
  // Para evitarlo, lo reposicionamos manualmente y bloqueamos la interacción
  // por unos milisegundos hasta que se estabiliza.
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !currentPet) return;

    const disableInteraction = () => {
      swiper.allowSlideNext = false;
      swiper.allowSlidePrev = false;
      swiper.allowTouchMove = false;
    };

    const enableInteraction = () => {
      swiper.allowSlideNext = true;
      swiper.allowSlidePrev = true;
      swiper.allowTouchMove = true;
    };

    swiper.slideTo(0, 300);
    disableInteraction();

    const timeout = setTimeout(() => {
      enableInteraction();
    }, 350);

    return () => clearTimeout(timeout);
  }, [currentPet]);

  // Estado de carga
  const isLoading = isValidating || tutorialDone === undefined;

  return {
    // Estado
    user,
    pets,
    currentPet,
    sortedPets,
    isLoading,
    isValidating,
    isRefreshing,

    // Swiper
    swiperConfig,

    // Modal
    modal: {
      isOpen,
      content,
      openModal,
      closeModal,
      ...modalProps,
    },

    // Alert
    alert: {
      ...alertState,
      hideAlert,
    },

    // Acciones de mascota
    petActions: {
      openNuevoSiniestro: guardCoverageAction(openNuevoSiniestro),
      openHistorial: guardCoverageAction(openHistorial),
      openTelemedicina: guardCoverageAction(openTelemedicina),
      openPlanPayment,
    },

    // Handlers
    handlePetSelect,
    handleRefresh,
    showPersonalDataAlert,
    handleAddPet,
  };
};
